import { useCallback, useEffect, useMemo } from 'react';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { useNavigate } from 'react-router-dom';
import { useUrlString, useUrlNumber } from '../../hooks/useUrlState';
import { Check, X, PauseCircle, Pencil, Eye, Search, IndianRupee, Hourglass, AlarmClock, Inbox, UserX } from 'lucide-react';
import { request } from '../../api/client';
import { usePermission, useAnyPermission } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { formatIst } from '../../lib/time';
import { Alert, Button, FilterResetButton, Input, Select, Modal, Textarea, Tooltip, StatCard, Tag, EntityAvatar, Pagination, message, type TagTone } from '../../ui';
import { Pill } from '../../components/TwDataTable';
import { SortableTh } from '../../components/SortableTh';
import { useServerSort } from '../../hooks/useServerSort';
import { RetailerLink } from '../../components/EntityLinks';
import { RoleBadge } from '../../components/RoleBadge';
import { TerritoryChips, useTerritoryLookup } from '../../components/TerritoryChips';
import { ChannelTag, CHANNELS } from '../../components/ChannelTag';
import { inr2 } from '../../lib/format';
import { ApproveOrderModal } from './ApproveOrderModal';
import { inr as inrWhole } from '../../lib/format';

// The shared formatter: a negative reads "-₹480", never "₹-480".
const inr = inrWhole;

/**
 * What an order is WORTH — the net payable when an advance cash discount is estimated on it, the
 * gross otherwise. The same expression the server sums for the KPI band (`ORDER_VALUE_SQL`), so the
 * Total column adds up to the tile above it instead of to a different definition of the same word.
 */
const orderValue = (o: any) =>
  (o?.advanceCdStatus === 'ESTIMATED' && o?.netGrandTotal != null ? Number(o.netGrandTotal) : Number(o?.grandTotal ?? 0));

/** Rows per page on first open. The queue pages on the SERVER, so this is the size of one request
 *  rather than a ceiling on what the reader can reach. */
const DEFAULT_PAGE_SIZE = 25;

/**
 * How long an order has sat unanswered, as a colour.
 *
 * Everything in this queue is "pending" — that word tells a decider nothing about which row to open.
 * What ranks the work is AGE, and the old table buried it in a grey date column identical to every
 * other cell. Now the row carries a left bar: green while it is same-day, gold once a night has
 * passed, red past three days. The thresholds are deliberately coarse — the point is triage, not a
 * stopwatch.
 */
const AGE = [
  {
    key: 'today', maxDays: 1, label: 'today',
    // The KPI tile's wording, kept in the SAME row as the chip's — the tile, the filter it applies
    // and the chip on the rows it opens are then one definition, and cannot describe different
    // windows. `tile` says "last 24 hours" rather than "today" because that is what `maxDays: 1`
    // measures: elapsed time, so an order placed at 11 PM yesterday is still in this band at 10 AM.
    tile: 'Placed in the last 24 hours', tone: 'sky' as const,
    bar: 'bg-brand-500', chip: 'border-brand-500/30 bg-brand-500/10 text-brand-700 dark:text-brand-300',
  },
  {
    key: 'waiting', maxDays: 3, label: 'waiting',
    tile: 'Waiting 1–2 days', tone: 'warning' as const,
    bar: 'bg-harvest-400', chip: 'border-harvest-400/40 bg-harvest-400/15 text-harvest-700 dark:text-harvest-300',
  },
  {
    key: 'overdue', maxDays: Infinity, label: 'overdue',
    tile: 'Overdue — 3 days or more', tone: 'danger' as const,
    bar: 'bg-danger-500', chip: 'border-danger-500/30 bg-danger-500/10 text-danger-600 dark:text-danger-500',
  },
] as const;
type AgeKey = (typeof AGE)[number]['key'];
const daysWaiting = (d: any) => Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000));
const ageOf = (d: any) => AGE.find((a) => daysWaiting(d) < a.maxDays) ?? AGE[AGE.length - 1];
const ageByKey = (k: AgeKey) => AGE.find((a) => a.key === k)!;

/* The three bands as the KPI band shows them: urgency first, because that is the order a decider
   works in — what is about to go bad, what already has, then what has only just arrived. Each glyph
   is its own: one hourglass repeated on two tiles marked nothing (see the "a constant glyph is
   noise" rule) — the alarm clock is the band that has run out of time. */
const BAND_TILES: { key: AgeKey; icon: JSX.Element }[] = [
  { key: 'waiting', icon: <Hourglass size={18} /> },
  { key: 'overdue', icon: <AlarmClock size={18} /> },
  { key: 'today', icon: <Inbox size={18} /> },
];

// Approval decisions that need a reason. Each maps to its dedicated endpoint.
type ApprovalAction = 'reject' | 'hold' | 'correction';
const ACTION_META: Record<ApprovalAction, { path: string; title: string; ok: string; done: string; danger?: boolean }> = {
  reject: { path: 'reject', title: 'Reject order', ok: 'Reject', done: 'Order rejected', danger: true },
  hold: { path: 'hold', title: 'Put order on hold', ok: 'Hold', done: 'Order put on hold' },
  correction: { path: 'request-correction', title: 'Request correction', ok: 'Send back', done: 'Sent back for correction' },
};

/**
 * Order Approval Queue — every PLACED order awaiting a decision.
 *
 * EVERYTHING ON THIS PAGE IS COUNTED AND CUT BY THE SERVER. The queue used to arrive as one capped
 * 200-row fetch that the browser then filtered, counted and scrolled: honest only while the backlog
 * stayed under the cap, and silently wrong past it — the 201st order was in no tile, no filter and
 * no page. Now `GET /orders` takes the page, the sort and every filter, and `GET /orders/approval-
 * summary` returns what the KPI tiles count and what the pickers offer, both over the whole queue.
 *
 * Three things follow from that and are easy to undo by accident:
 *  • nothing on screen may be derived from the rows in hand — they are one page of the queue;
 *  • a filter must narrow the QUERY and go back to page one, or it narrows a page instead of a queue;
 *  • the picker LABELS come from the summary too, because the masters they used to be built from
 *    (`/retailers`, `/users`) are themselves paged — which is how a dealer became "#41f0…".
 */
export function OrderApprovalQueuePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canViewRetailers = usePermission('RETAILER.VIEW');
  /*
   * Rejecting an order is TERMINAL, so for everyone except the person who decides such a request it
   * is an ASK: the server records it and a Super Admin rules on it. Mirrors
   * OrderRejectRequestService.needsApproval — the server is still the authority, this only decides
   * what the dialog promises.
   */
  const canDecideReject = usePermission('ORDER.REJECT.APPROVE');
  const [actionFor, setActionFor] = useState<{ id: string; action: ApprovalAction } | null>(null);
  const [approveFor, setApproveFor] = useState<{ id: string; orderNo?: string } | null>(null);
  const [reason, setReason] = useState('');

  /* ---- filters ----
     Every one of these reaches the SERVER. They used to be applied in the browser over one capped
     200-row fetch, which is a filter that quietly stops at row 200: past the cap a dealer with
     orders in the queue matched nothing, and the count beside the toolbar said so with confidence.
     All of them stay in the URL, so Back restores the view and a filtered queue is a link. */
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 300);
  const [channel, setChannel] = useUrlString('channel', 'ALL');
  const [salesPersonId, setSalesPersonId] = useUrlString('sp', 'ALL');
  const [retailerId, setRetailerId] = useUrlString('retailer', 'ALL');
  // Which age band the KPI tiles have drilled into — 'ALL' or one of the AGE keys. URL-backed like
  // every other filter here, so a decider can send "the overdue ones" as a link and Back restores it.
  const [ageBand, setAgeBand] = useUrlString<AgeKey | 'ALL'>('age', 'ALL');
  const [minInput, setMinInput] = useUrlString('min');
  const [maxInput, setMaxInput] = useUrlString('max');
  const minTotal = useDebounce(minInput, 400);
  const maxTotal = useDebounce(maxInput, 400);

  /* Which page of the queue is on screen. In the URL like every filter, so "page 3 of the overdue
     ones" is something a decider can send or come back to. */
  const [page, setPage] = useUrlNumber('page', 1);
  const [pageSize, setPageSize] = useUrlNumber('pageSize', DEFAULT_PAGE_SIZE);
  /** Narrowing the queue must restart it — page 3 of the whole queue is past the end of most filters. */
  const firstPage = useCallback(() => setPage(1), [setPage]);
  /** Wrap a filter's setter so changing it also returns to page one. */
  const onFilter = <T,>(set: (v: T) => void) => (v: T) => { set(v); firstPage(); };

  // The sort orders the WHOLE queue in SQL before the page is cut from it — "biggest order first"
  // has to mean the biggest of 39, not the biggest of the 25 that happen to be on screen.
  const { sortState, onSortChange, sort } = useServerSort({ onChange: firstPage });

  /** Everything the server needs to reproduce this view. One object, so the table, the KPI band and
   *  the pickers cannot be asking about different sets of orders. */
  const filters = useMemo(() => ({
    status: 'PLACED',
    search: search.trim() || undefined,
    channel: channel === 'ALL' ? undefined : channel,
    salesPersonId: salesPersonId === 'ALL' ? undefined : salesPersonId,
    retailerId: retailerId === 'ALL' ? undefined : retailerId,
    ageBand: ageBand === 'ALL' ? undefined : ageBand,
    minTotal: minTotal.trim() || undefined,
    maxTotal: maxTotal.trim() || undefined,
  }), [search, channel, salesPersonId, retailerId, ageBand, minTotal, maxTotal]);

  const { data, isLoading } = useQuery({
    queryKey: ['orders-pending-approval', filters, sort, page, pageSize],
    queryFn: () => request<any>({ url: '/orders', params: { ...filters, page, pageSize, ...(sort ? { sort } : {}) } }),
    placeholderData: keepPreviousData,
  });
  const rows: any[] = data?.data ?? [];
  /** The queue's real size, from the server's COUNT — what the pager pages through. */
  const total = Number(data?.meta?.total ?? 0);

  /* A page past the end is nobody's view. Filters restart the queue where they are set, but a link
     can still arrive at ?page=7 for a queue that now holds two pages — which rendered an empty table
     under a KPI band counting dozens. Landing on the LAST page keeps the rows and the count agreeing. */
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (!isLoading && page > lastPage) setPage(lastPage);
  }, [isLoading, page, lastPage, setPage]);

  /*
   * The KPI band and the filter facets, counted over the WHOLE queue in SQL.
   *
   * Its own query, and deliberately not derived from the rows above: the table holds one page, so
   * anything computed from it would describe 25 orders and label the answer 39. The server drops the
   * age filter for the tiles (they ARE the age breakdown) and each facet's own dimension for that
   * picker's counts — see OrderService.approvalQueueSummary.
   */
  const { data: summary } = useQuery({
    queryKey: ['orders-approval-summary', filters],
    queryFn: () => request<any>({ url: '/orders/approval-summary', params: filters }),
    placeholderData: keepPreviousData,
  });

  /* WHERE the order comes from. A dealer is pinned to ONE territory node (its beat, usually) and
     the levels above it are read by walking the parent chain, so the chips need the master — loaded
     once per session by the shared hook and re-used by every register that shows a customer. */
  const { byId: territoryMap } = useTerritoryLookup();

  /**
   * The dealer cell: an identity mark, the name linked to its dealer page, then where it sits.
   *
   * The mark is back, and it is INITIALS rather than the shared Store glyph that was removed from
   * here before — that one said the same thing on every row, which is the opposite of an identity
   * mark. Initials on a hue derived from the NAME differ per dealer, and are the same colour for
   * that dealer on every other page in the app.
   *
   * Only the NAME is the link — the address is context, not a second destination. `RetailerLink`
   * stops propagation, so clicking the name opens the dealer instead of the row's order, and it
   * degrades to plain text for anyone without RETAILER.VIEW.
   */
  const renderRetailer = (o: any) => {
    const name = o.companyName;
    if (!name) return <span className="text-subtle">#{o.retailerId}</span>;
    return (
      <div className="flex min-w-0 items-start gap-2">
        <EntityAvatar name={name} size={30} square className="mt-0.5" />
        <div className="min-w-0">
          {/* The dealer code beside the name it identifies. This column is the widest on the page and
              had room for it; an approver reading a queue of similarly-named shops needs the number
              that tells them apart without opening the order. */}
          <div className="flex min-w-0 items-baseline gap-1.5">
            <span className="truncate"><RetailerLink id={o.retailerId}>{name}</RetailerLink></span>
            {o.retailerCode && <span className="shrink-0 type-caption text-subtle">{o.retailerCode}</span>}
          </div>
          {/* Second line, so a long dealer name never fights the capsules for width. */}
          <div className="mt-1"><TerritoryChips territoryId={o.retailerTerritoryId} city={o.retailerCity} /></div>
        </div>
      </div>
    );
  };

  /*
   * THE PICKERS.
   *
   * Each option carries a COUNT, and that count is a promise: it is how many orders you are left
   * with if you click it, every OTHER filter already applied. The server counts them that way (each
   * facet is blind only to its own dimension), which is the exact inverse of how the KPI tiles above
   * are counted — a tile describes the queue, an option predicts the click.
   *
   * Two consequences worth keeping:
   *  • an option that would leave nothing is not offered at all — a filter must never hand the
   *    reader an empty result;
   *  • except the one already SELECTED, which stays on the list even at zero, or the control would
   *    strand itself with no way back.
   */
  const facetOpts = <T extends { count: number }>(
    items: T[],
    selected: string,
    idOf: (x: T) => string,
    render: (x: T) => { label: JSX.Element; title: string },
    allLabel: string,
  ) => {
    const kept = items.filter((x) => x.count > 0 || idOf(x) === selected);
    const opts = kept.map((x) => {
      const { label, title } = render(x);
      return {
        value: idOf(x),
        title,
        // The trigger shows the plain name; the dropdown row shows the full identity. A collapsed
        // control has one line to say what is filtered, and chips wrap it onto three.
        selectedText: title,
        label: (
          <span className="flex w-full items-center gap-2">
            <span className="min-w-0 flex-1">{label}</span>
            <span className="shrink-0 tabular-nums type-caption text-muted">{x.count}</span>
          </span>
        ),
      };
    });
    return [{ value: 'ALL', label: allLabel, title: allLabel }, ...opts];
  };

  const facets = summary?.facets;
  const channelOpts = useMemo(
    () => facetOpts(
      [...(facets?.channels ?? [])].sort((a: any, b: any) => b.count - a.count),
      channel,
      (c: any) => String(c.value),
      (c: any) => ({
        label: <ChannelTag channel={c.value} />,
        title: CHANNELS[String(c.value).toUpperCase()]?.label ?? String(c.value),
      }),
      'All channels',
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facets, channel],
  );

  const salesPersonOpts = useMemo(
    () => facetOpts(
      [...(facets?.salesPersons ?? [])].sort((a: any, b: any) => String(a.name ?? '').localeCompare(String(b.name ?? ''))),
      salesPersonId,
      (u: any) => String(u.id),
      (u: any) => ({
        // Name AND role: this list mixes tiers on purpose — a Sales Head owns orders under the
        // "a head is a senior rep" model — and two people under one unlabelled list read as peers.
        label: (
          <span className="flex min-w-0 items-center gap-2">
            <EntityAvatar name={u.name ?? `#${u.id}`} size={22} />
            <span className="truncate">{u.name ?? `#${u.id}`}</span>
            {u.roleCode && <RoleBadge role={u.roleCode} />}
          </span>
        ),
        title: u.name ?? `#${u.id}`,
      }),
      'All sales persons',
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facets, salesPersonId],
  );

  const retailerOpts = useMemo(
    () => facetOpts(
      [...(facets?.retailers ?? [])].sort((a: any, b: any) => String(a.companyName ?? '').localeCompare(String(b.companyName ?? ''))),
      retailerId,
      (r: any) => String(r.id),
      (r: any) => ({
        // The same identity mark and the same address chips the table row wears, so picking a dealer
        // here and finding it in the list are the same act of recognition. The location used to be
        // dot-joined onto the end of the name ("BALAJI SEEDS, PIMPALNER · Pimpalner"), which reads
        // as more name.
        label: (
          <span className="flex min-w-0 items-center gap-2">
            <EntityAvatar name={r.companyName ?? `#${r.id}`} size={22} square />
            <span className="min-w-0">
              <span className="block truncate">{r.companyName ?? `#${r.id}`}</span>
              <TerritoryChips territoryId={r.territoryId} city={r.city} />
            </span>
          </span>
        ),
        title: [r.companyName ?? `#${r.id}`, r.city].filter(Boolean).join(' · '),
      }),
      'All retailers',
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facets, retailerId, territoryMap],
  );

  /*
   * Orders nobody's name will find.
   *
   * A sales person is not mandatory on an order (a dealer's own app order has none), so the picker
   * is a PARTIAL cover of the queue — and a filter that silently covers part of a list is the kind
   * of thing a reader discovers by getting the wrong answer. Counted from the facets themselves:
   * the queue, minus everything the names between them account for.
   */
  const namedByRep = (facets?.salesPersons ?? []).reduce((n: number, u: any) => n + Number(u.count || 0), 0);
  const unassigned = Math.max(0, Number(summary?.total ?? 0) - namedByRep);

  const hasFilters = !!(searchInput || channel !== 'ALL' || salesPersonId !== 'ALL' || retailerId !== 'ALL' || ageBand !== 'ALL' || minInput || maxInput);
  const clearFilters = () => {
    setSearchInput(''); setChannel('ALL'); setSalesPersonId('ALL'); setRetailerId('ALL'); setAgeBand('ALL'); setMinInput(''); setMaxInput('');
    firstPage();
  };
  // A second click on the tile you are already in puts the whole queue back — the tile is the way out
  // as well as the way in, so nobody has to hunt for Clear to undo one tap.
  const toggleAge = (k: AgeKey) => { setAgeBand((cur) => (cur === k ? 'ALL' : k)); firstPage(); };

  // Refresh the queue AND the order's detail views so the detail page shows the new status without a reload.
  const refreshOrder = (id: string) => {
    qc.invalidateQueries({ queryKey: ['orders-pending-approval'] });
    qc.invalidateQueries({ queryKey: ['orders'] });
    qc.invalidateQueries({ queryKey: ['order', id] });
    qc.invalidateQueries({ queryKey: ['order-timeline', id] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };

  const actionMut = useConfirmedMutation((v) => ({
    title: `${ACTION_META[v.action].title}?`,
    // Rejecting is terminal and — for anyone who cannot decide such a request — is an ASK, not an act.
    // The confirmation must not promise something the server will not do.
    content: v.action === 'reject' && !canDecideReject
      ? 'This goes to the Super Admin to decide. The order is frozen until they do — it cannot be approved, held or sent back.'
      : v.action === 'reject'
        ? 'This ends the order for good — there is no way back from rejected, and the dealer is told your reason.'
        : 'The order moves out of this queue and whoever placed it is told.',
    okText: v.action === 'reject' && !canDecideReject ? 'Yes, ask the Super Admin' : `Yes, ${ACTION_META[v.action].ok.toLowerCase()}`,
    danger: !!ACTION_META[v.action].danger,
  }), {
    mutationFn: ({ id, action, reason }: { id: string; action: ApprovalAction; reason: string }) =>
      request<any>({ url: `/orders/${id}/${ACTION_META[action].path}`, method: 'POST', data: { reason } }),
    onSuccess: (d: any, { id, action }) => {
      // `requested: true` is a SUCCESS, not a failure — the ask was recorded. Saying "Order rejected"
      // here would be a lie the queue itself contradicts a second later, when the order is still in it.
      message.success(d?.requested
        ? 'Rejection sent to the Super Admin — the order is frozen until they decide'
        : ACTION_META[action].done);
      setActionFor(null); setReason('');
      refreshOrder(id);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const stop = (e: any) => e.stopPropagation();
  const meta = actionFor ? ACTION_META[actionFor.action] : null;

  /* What the queue is worth and how stale it is — the server's figures, over the WHOLE queue.
     It used to be rolled up here from the rows on screen, which was only ever true while the queue
     fitted in one fetch; now the table holds one page and a roll-up of it would describe 25 orders
     under a heading that says 39.

     The bands still PARTITION the headline exactly — last 24 hours + 1–2 days + 3 days or more is
     every order in it, and their money adds up to its rupees — because the server sums the headline
     FROM the three bands rather than counting it separately. */
  const bands: Record<AgeKey, { count: number; value: number; oldest: string | null }> =
    summary?.bands ?? {
      today: { count: 0, value: 0, oldest: null },
      waiting: { count: 0, value: 0, oldest: null },
      overdue: { count: 0, value: 0, oldest: null },
    };

  /**
   * What each band's hint says under its number.
   *
   * The money first, because that is what makes a decision urgent, and — for the band that has run
   * out of time — how long the worst of it has waited, which is the one age figure on the page an
   * approver can act on. Days are counted HERE, from the band's oldest order date, with the same
   * function the row chips use, so "oldest waiting 67 days" and the "67d overdue" chip on the row it
   * refers to can never differ by a day.
   */
  const bandHint = (k: AgeKey, b: { count: number; value: number; oldest: string | null }) => {
    if (!b.count) {
      return { waiting: 'Nothing has waited this long', overdue: 'Nothing has crossed 3 days', today: 'No orders placed in the last 24 hours' }[k];
    }
    if (k === 'overdue') return `${inr(b.value)} held up · oldest waiting ${daysWaiting(b.oldest)} days`;
    if (k === 'waiting') return `${inr(b.value)} still to be decided`;
    return `${inr(b.value)} newly arrived`;
  };

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <h1 className="type-page-title text-content">Order Approval Queue</h1>
        <Pill color="blue">{Number(summary?.total ?? 0)} pending</Pill>
      </div>

      {/* Money first — it is what makes a decision urgent — then the age that ranks the work.
          Every tile is a filter: the three age tiles open their own band, and the money tile puts
          the whole queue back. "Waiting longest" used to sit here as a number nobody could act on
          (64d, and then what?); it now rides in the overdue tile's hint, which is where you go to
          do something about it. A band with nothing in it is not clickable — no dead ends. */}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          tone="gold" icon={<IndianRupee size={18} />} label="Order value awaiting a decision" value={inr(summary?.value ?? 0)}
          hint={hasFilters ? 'Total for the filters you have set — tap to clear them' : 'Total of every order waiting for a decision'}
          active={!hasFilters}
          onClick={hasFilters ? clearFilters : undefined}
        />
        {/* The three bands, from one table — so the tile's wording, the filter it applies and the
            chip on the rows it opens are the same definition rather than three copies of it. */}
        {BAND_TILES.map(({ key, icon }) => (
          <StatCard
            key={key}
            tone={bands[key].count ? ageByKey(key).tone : 'neutral'}
            icon={icon}
            label={ageByKey(key).tile}
            value={bands[key].count}
            hint={bandHint(key, bands[key])}
            active={ageBand === key}
            onClick={bands[key].count ? () => toggleAge(key) : undefined}
          />
        ))}
      </div>

      {/* Filters — in COLUMN order, so the control for a column is found by looking under it.
          Every one of them narrows the QUERY and returns the reader to page one: the rows beneath
          them changed, and page 3 of the whole queue is past the end of most filters. */}
      <div className="mb-3 rounded-card border border-line bg-surface shadow-e1 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
            <Input value={searchInput} onChange={(e) => { setSearchInput(e.target.value); firstPage(); }} placeholder="Order no / retailer…" size="sm" className="w-52 pl-8" />
          </div>
          <Select value={channel} onChange={onFilter(setChannel)} options={channelOpts} size="sm" className="w-44" popupMatchSelectWidth={false} />
          <Select
            value={salesPersonId} onChange={onFilter(setSalesPersonId)} options={salesPersonOpts}
            size="sm" className="w-48" showSearch optionFilterProp="title" popupMatchSelectWidth={false}
          />
          {canViewRetailers && (
            <Select
              value={retailerId} onChange={onFilter(setRetailerId)} options={retailerOpts}
              size="sm" className="w-56" showSearch optionFilterProp="title" popupMatchSelectWidth={false}
            />
          )}
          <div className="flex items-center gap-1 text-sm text-muted">
            <span className="text-xs">₹</span>
            <Input type="number" value={minInput} onChange={(e) => { setMinInput(e.target.value); firstPage(); }} placeholder="Min" size="sm" className="w-20" />
            <span className="text-subtle">–</span>
            <Input type="number" value={maxInput} onChange={(e) => { setMaxInput(e.target.value); firstPage(); }} placeholder="Max" size="sm" className="w-20" />
          </div>
          {hasFilters && <FilterResetButton onClick={clearFilters} />}
        </div>
        {/* What the numbers beside each name mean, said once rather than guessed at. */}
        <div className="mt-2 type-caption text-subtle">
          The number beside an option is how many orders it leaves, with your other filters applied.
        </div>
        {/* The gap in the sales-person filter, named. Without this the picker looks like a cover of
            the queue and quietly is not one. */}
        {unassigned > 0 && (
          <div className="mt-1.5 flex items-center gap-1.5 type-caption text-warning-600 dark:text-warning-500">
            <UserX size={13} />
            {unassigned} order{unassigned === 1 ? '' : 's'} here {unassigned === 1 ? 'has' : 'have'} no sales person on {unassigned === 1 ? 'it' : 'them'} — no name in that filter will find {unassigned === 1 ? 'it' : 'them'}.
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-card border border-line bg-surface shadow-e1">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="gb-thead-tint">
            <tr className="border-b border-line text-left type-overline text-muted">
              <SortableTh sortKey="orderNo" state={sortState} onSort={onSortChange} className="px-3 py-2.5 pl-4">Order No</SortableTh>
              <SortableTh sortKey="orderDate" state={sortState} onSort={onSortChange} className="px-3 py-2.5">Date</SortableTh>
              <th className="px-3 py-2.5">Retailer</th>
              <SortableTh sortKey="channel" state={sortState} onSort={onSortChange} className="px-3 py-2.5">Channel</SortableTh>
              <SortableTh sortKey="subtotal" align="right" state={sortState} onSort={onSortChange} className="px-3 py-2.5">Subtotal</SortableTh>
              <SortableTh sortKey="grandTotal" align="right" state={sortState} onSort={onSortChange} className="px-3 py-2.5">Total</SortableTh>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="px-3 py-10 text-center text-muted">Loading…</td></tr>}
            {!isLoading && !rows.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-muted">{hasFilters ? 'No orders match these filters.' : 'No orders awaiting approval 🎉'}</td></tr>}
            {rows.map((o) => (
              <tr key={o.id} className="cursor-pointer border-b border-line/70 hover:bg-surface-2" onClick={() => navigate(`/orders/${o.id}`)}>
                <td className="relative whitespace-nowrap px-3 py-2.5 pl-4 font-medium text-brand-700 dark:text-brand-400">
                  <span className={`absolute inset-y-0 left-0 w-1 ${ageOf(o.orderDate).bar}`} aria-hidden />
                  {o.orderNo}
                  {/* Named on the row, not only in a tooltip: the reason four buttons are dead has to
                      be readable without hovering each one. */}
                  {o.rejectRequest && (
                    <div className="mt-0.5"><Pill color="red">Rejection with the Super Admin</Pill></div>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5">
                  <div className="text-muted">{formatIst(o.orderDate)}</div>
                  <span className={`mt-0.5 inline-flex items-center rounded-pill border px-2 type-caption leading-5 ${ageOf(o.orderDate).chip}`}>
                    {daysWaiting(o.orderDate) === 0 ? 'today' : `${daysWaiting(o.orderDate)}d ${ageOf(o.orderDate).label}`}
                  </span>
                </td>
                {/* Name, then where it sits in the territory tree. The identity mark that used to
                    sit here is gone: it is meant to differ per row so the eye can find its place
                    again, and forced to one shared Store glyph it said the same thing on every row
                    — which the column header already does — while pushing the names right. */}
                <td className="min-w-0 px-3 py-2.5 text-content">
                  {renderRetailer(o)}
                </td>
                {/* How the order reached us, from the one definition the order detail page uses — a
                    globe for the web desk, a storefront for a dealer's own app. This column printed
                    the raw enum (`RETAILER_APP`) against a colour map for two channel names that do
                    not exist, so every pill on the page was the same grey. */}
                <td className="px-3 py-2.5"><ChannelTag channel={o.channel} /></td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-muted">{inr(o.subtotal)}</td>
                {/* An order with an ESTIMATED advance cash discount is worth its NET payable — the
                    figure the KPI band sums and the one the register leads with. Printing gross here
                    would leave the column unable to add up to the tile above it. The original is
                    still one hover away, because it is what the dealer was originally quoted. */}
                <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums text-content">
                  {o.advanceCdStatus === 'ESTIMATED' && o.netGrandTotal != null ? (
                    <Tooltip label={`Net payable ${inr2(o.netGrandTotal)} after the advance cash discount (estimated · pending lock). Original ${inr2(o.grandTotal)}.`}>
                      <span className="underline decoration-dotted underline-offset-2">{inr(orderValue(o))}</span>
                    </Tooltip>
                  ) : inr(orderValue(o))}
                </td>
                {/* Icon-only row actions (32px squares, tooltip + aria-label each). Five labelled
                    buttons ran ~450px wide on every row and turned the queue into a wall of chrome;
                    the decisions are the same five in the same order on every row, so the icons are
                    learnt once. Intent stays in the COLOUR: filled green approve, soft-red reject,
                    quiet grey for the rest. Nothing fires blind — each opens its dialog. */}
                <td className="px-3 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-1" onClick={stop}>
                    {/* An order with a rejection waiting on the Super Admin is frozen: the server
                        refuses every decision on it (ORDER_REJECT_PENDING), so the buttons must
                        refuse too. Only "open the order" survives — that is where the ask is shown. */}
                    <Tooltip label={o.rejectRequest ? 'Frozen — a rejection is waiting for the Super Admin' : 'Approve order'}>
                      <span>
                        <Button size="sm" variant="primary" aria-label="Approve order" disabled={!!o.rejectRequest} icon={<Check size={16} />} onClick={() => setApproveFor({ id: o.id, orderNo: o.orderNo })} />
                      </span>
                    </Tooltip>
                    <Tooltip label={o.rejectRequest ? 'A rejection is already waiting for the Super Admin' : (canDecideReject ? 'Reject order' : 'Ask the Super Admin to reject this order')}>
                      <span>
                        <Button size="sm" variant="soft-danger" aria-label="Reject order" disabled={!!o.rejectRequest} icon={<X size={16} />} onClick={() => setActionFor({ id: o.id, action: 'reject' })} />
                      </span>
                    </Tooltip>
                    <Tooltip label={o.rejectRequest ? 'Frozen — a rejection is waiting for the Super Admin' : 'Put on hold (stock / credit pending)'}>
                      <span>
                        <Button size="sm" variant="text" aria-label="Put on hold" disabled={!!o.rejectRequest} icon={<PauseCircle size={16} />} onClick={() => setActionFor({ id: o.id, action: 'hold' })} />
                      </span>
                    </Tooltip>
                    <Tooltip label={o.rejectRequest ? 'Frozen — a rejection is waiting for the Super Admin' : 'Send back to the placer for correction'}>
                      <span>
                        <Button size="sm" variant="text" aria-label="Send back for correction" disabled={!!o.rejectRequest} icon={<Pencil size={16} />} onClick={() => setActionFor({ id: o.id, action: 'correction' })} />
                      </span>
                    </Tooltip>
                    <Tooltip label="Open the order">
                      <Button size="sm" variant="text" aria-label="Open the order" icon={<Eye size={16} />} onClick={() => navigate(`/orders/${o.id}`)} />
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {/* The pager sits INSIDE the card but OUTSIDE the horizontal scroller — within it, a wide
            table scrolls the controls sideways off the screen. Its total is the server's COUNT over
            the whole queue, so the page numbers reach every order rather than the first fetch of
            them. */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-3 py-2.5">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            disabled={isLoading}
            showTotal={(t, [from, to]) => (t === 0 ? 'No orders' : `${from}–${to} of ${t} order${t === 1 ? '' : 's'}`)}
            onChange={(pg, ps) => { setPage(ps === pageSize ? pg : 1); setPageSize(ps); }}
          />
        </div>
      </div>

      {/* Reason modal — reject / hold / correction */}
      <Modal
        open={!!actionFor}
        onOpenChange={(open) => { if (!open) { setActionFor(null); setReason(''); } }}
        title={meta?.title ?? ''}
        width={460}
        footer={
          <>
            <Button variant="text" onClick={() => { setActionFor(null); setReason(''); }}>Cancel</Button>
            <Button
              variant={meta?.danger ? 'danger' : 'primary'}
              disabled={!reason.trim()}
              loading={actionMut.isPending}
              onClick={() => actionFor && actionMut.mutate({ id: actionFor.id, action: actionFor.action, reason: reason.trim() })}
            >
              {meta?.ok ?? 'OK'}
            </Button>
          </>
        }
      >
        {/* Say up front where this is going. A reason typed for the Super Admin to rule on is written
            differently from one typed to close a queue row — and the dealer reads it either way. */}
        {actionFor?.action === 'reject' && !canDecideReject && (
          <Alert type="warning" style={{ marginBottom: 12 }}
            title="A Super Admin decides this."
            description="Rejecting ends the order for good, so it is asked for, not done. Your reason is what they rule on and what the dealer is told. The order is frozen until they decide." />
        )}
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
          placeholder={actionFor?.action === 'reject' && !canDecideReject
            ? 'Why should this order be rejected? (mandatory — the Super Admin rules on this)'
            : 'Reason (mandatory — visible in audit + status history)'} />
      </Modal>

      <ApproveOrderModal
        open={!!approveFor}
        orderId={approveFor?.id ?? null}
        orderNo={approveFor?.orderNo}
        onClose={() => setApproveFor(null)}
      />
    </div>
  );
}
